// Probe registry: probes register here and are selected to run purely by configuration.

import { blocked, tierAtMost } from './types';
import type { Probe, ProbeContext, ProbeMeasurement, ProbeTier } from './types';

/** Configuration that selects which registered probes run for a given page/run. */
export interface EngineConfig {
  /** If set, only these probe ids are eligible; otherwise every registered probe is. */
  readonly enabledProbeIds?: readonly string[];
  /** Optional tier ceiling — e.g. the anonymous public test runs `free` only. */
  readonly maxTier?: ProbeTier;
  /**
   * Include probes that require a permission prompt. Off by default so the public auto-test
   * (and any run the user hasn't opted into) never triggers a prompt (PRD §6.2).
   */
  readonly includePermissioned?: boolean;
}

/**
 * Holds the registered probes. Which probes actually run is decided by <see cref="select"/>
 * from an <see cref="EngineConfig"/>, so enabling/disabling a probe is configuration, not code.
 */
export class ProbeRegistry {
  private readonly probes = new Map<string, Probe>();

  /** Registers a probe. Throws if its id is already taken (ids must be unique). */
  register(probe: Probe): void {
    if (this.probes.has(probe.id)) {
      throw new Error(`A probe with id "${probe.id}" is already registered.`);
    }
    this.probes.set(probe.id, probe);
  }

  unregister(id: string): boolean {
    return this.probes.delete(id);
  }

  get(id: string): Probe | undefined {
    return this.probes.get(id);
  }

  all(): readonly Probe[] {
    return [...this.probes.values()];
  }

  /** Selects the probes to run for a config — disabling one is just leaving it out. */
  select(config: EngineConfig = {}): Probe[] {
    const allowed = config.enabledProbeIds ? new Set(config.enabledProbeIds) : null;
    return this.all().filter(
      (probe) =>
        (allowed === null || allowed.has(probe.id)) &&
        (config.maxTier === undefined || tierAtMost(probe.tier, config.maxTier)) &&
        (config.includePermissioned === true || !probe.permissionRequired),
    );
  }
}

/**
 * Runs a probe and guarantees a first-class result: any thrown error or rejected promise is
 * converted to a blocked measurement, so one misbehaving probe never breaks the whole run.
 */
export async function runProbe(probe: Probe, context: ProbeContext): Promise<ProbeMeasurement> {
  try {
    return await probe.run(context);
  } catch (error) {
    const note = error instanceof Error ? error.message : 'probe threw';
    return blocked(probe.id, note);
  }
}

/**
 * Runs every probe safely and returns their measurements, order-preserving. Light probes run
 * in parallel; HEAVY probes (the throughput tests, which saturate the network) run one at a
 * time after the light set settles, so throughput isn't measured while fighting itself and
 * latency/jitter/loss aren't measured under artificial load.
 */
export async function runAll(
  probes: readonly Probe[],
  context: ProbeContext,
): Promise<ProbeMeasurement[]> {
  const results = new Array<ProbeMeasurement>(probes.length);

  await Promise.all(
    probes.map((probe, index) =>
      probe.heavy ? Promise.resolve() : runProbe(probe, context).then((m) => { results[index] = m; }),
    ),
  );

  for (let index = 0; index < probes.length; index++) {
    if (probes[index].heavy) {
      results[index] = await runProbe(probes[index], context);
    }
  }

  return results;
}

/** The default registry that probe modules self-register into. */
export const registry = new ProbeRegistry();
